import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, MessageSquare, CalendarCheck, ShoppingBag, TrendingUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-line p-6">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="font-display font-extrabold text-3xl tracking-tighter text-ink">{value}</p>
      <p className="font-body text-sm text-stone">{label}</p>
    </div>
  );
}

function DataTable({ columns, rows, empty, testid }) {
  if (!rows.length) return <p className="text-stone font-body py-10 text-center">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-line bg-white" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line bg-muted-cream/50">
            {columns.map((c) => <th key={c.key} className="text-left font-body font-semibold text-ink px-4 py-3">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-line/60 last:border-0 hover:bg-cream/50">
              {columns.map((c) => <td key={c.key} className="px-4 py-3 font-body text-ink/80">{c.render ? c.render(r) : r[c.key] || "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminDashboard() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [appts, setAppts] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem("jp_token")) { nav("/admin/login"); return; }
    const load = async () => {
      try {
        const [s, l, c, a, o] = await Promise.all([
          api.get("/admin/stats"), api.get("/admin/leads"), api.get("/admin/contacts"),
          api.get("/admin/appointments"), api.get("/admin/orders"),
        ]);
        setStats(s.data); setLeads(l.data); setContacts(c.data); setAppts(a.data); setOrders(o.data);
      } catch (err) {
        if (err.response?.status === 401) { localStorage.removeItem("jp_token"); nav("/admin/login"); }
      }
      setLoading(false);
    };
    load();
  }, [nav]);

  const logout = () => { localStorage.removeItem("jp_token"); nav("/admin/login"); };

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-terracotta" /></div>;

  return (
    <div className="min-h-screen bg-cream grain">
      <header className="bg-ink text-cream">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
          <div className="font-display font-extrabold text-2xl tracking-tightest">Job<span className="text-terracotta">Propulse</span> <span className="font-body font-normal text-sm text-cream/50">· Admin</span></div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-5 py-2 font-body text-sm hover:bg-cream hover:text-ink transition-colors">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
        <h1 className="font-display font-extrabold text-4xl tracking-tightest text-ink mb-8">Tableau de bord</h1>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard icon={TrendingUp} label="Revenu total" value={`${(stats?.revenue || 0).toFixed(0)}€`} accent="bg-terracotta/10 text-terracotta" />
          <StatCard icon={Users} label="Leads" value={stats?.leads || 0} accent="bg-sage/15 text-sage" />
          <StatCard icon={MessageSquare} label="Messages" value={stats?.contacts || 0} accent="bg-ink/10 text-ink" />
          <StatCard icon={CalendarCheck} label="Rendez-vous" value={stats?.appointments || 0} accent="bg-sage/15 text-sage" />
          <StatCard icon={ShoppingBag} label="Commandes" value={stats?.orders || 0} accent="bg-terracotta/10 text-terracotta" />
        </div>

        <Tabs defaultValue="appointments">
          <TabsList className="bg-muted-cream mb-6 flex-wrap h-auto">
            <TabsTrigger value="appointments" data-testid="tab-appointments">Rendez-vous</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Commandes</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Messages</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments">
            <DataTable testid="table-appointments" empty="Aucun rendez-vous pour le moment." rows={appts} columns={[
              { key: "name", label: "Nom" },
              { key: "email", label: "Email" },
              { key: "date", label: "Date" },
              { key: "time", label: "Heure" },
              { key: "offer_slug", label: "Offre" },
              { key: "profile", label: "Profil" },
              { key: "status", label: "Statut", render: (r) => <span className="text-xs font-semibold text-sage bg-sage/10 px-2 py-1 rounded-full">{r.status}</span> },
            ]} />
          </TabsContent>

          <TabsContent value="orders">
            <DataTable testid="table-orders" empty="Aucune commande pour le moment." rows={orders} columns={[
              { key: "offer_title", label: "Offre" },
              { key: "customer_name", label: "Client" },
              { key: "customer_email", label: "Email" },
              { key: "amount", label: "Montant", render: (r) => `${(r.amount || 0).toFixed(2)}€` },
              { key: "status", label: "Statut", render: (r) => <span className="text-xs font-semibold text-terracotta bg-terracotta/10 px-2 py-1 rounded-full">{r.status}</span> },
              { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
            ]} />
          </TabsContent>

          <TabsContent value="contacts">
            <DataTable testid="table-contacts" empty="Aucun message pour le moment." rows={contacts} columns={[
              { key: "name", label: "Nom" },
              { key: "email", label: "Email" },
              { key: "subject", label: "Sujet" },
              { key: "message", label: "Message", render: (r) => <span className="line-clamp-2 max-w-xs block">{r.message}</span> },
              { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
            ]} />
          </TabsContent>

          <TabsContent value="leads">
            <DataTable testid="table-leads" empty="Aucun lead pour le moment." rows={leads} columns={[
              { key: "name", label: "Nom" },
              { key: "email", label: "Email" },
              { key: "profile", label: "Profil" },
              { key: "source", label: "Source" },
              { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
            ]} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
